import { TokenIcon } from './holdings-table'
import { CORE_TOKEN_LOGOS, SOL_MINT, USDC_MINT, USDT_MINT } from '@/lib/default-tokens'

const CORE: { mint: string; symbol: string }[] = [
  { mint: SOL_MINT, symbol: 'SOL' },
  { mint: USDC_MINT, symbol: 'USDC' },
  { mint: USDT_MINT, symbol: 'USDT' },
]

/**
 * Logo-and-ticker chips for the tokens the trading wallet holds (John's
 * rule, Oct 29 2026: logos and tickers, never the full words). Display
 * only; the pickers and swap chips stay interactive on their own.
 */
export function CoreAssetChips({ size = 'size-4', logosOnly = false, className = '' }: { size?: string; logosOnly?: boolean; className?: string }) {
  return (
    <span className={`inline-flex flex-wrap items-center gap-1.5 align-middle ${className}`}>
      {CORE.map((asset) => (
        <AssetChip key={asset.mint} symbol={asset.symbol} imageUrl={CORE_TOKEN_LOGOS[asset.mint]} size={size} logoOnly={logosOnly} />
      ))}
    </span>
  )
}

export type CoinChipOption<T extends string> = { value: T; symbol: string; logo?: string }

/**
 * Coins to pick from, as pills with the logo and the ticker, the chosen one
 * lit the way the platform group lights its pick (John, Nov 4 2026: the
 * segmented tabs were "small logos/names, and big tab selector"; and it is
 * SOL, not Solana, when the coin is meant).
 */
export function CoinChipGroup<T extends string>({ value, options, onChange, disabled = false }: { value: T; options: CoinChipOption<T>[]; onChange: (value: T) => void; disabled?: boolean }) {
  return (
    <div role="radiogroup" aria-label="Coin" className="flex flex-wrap gap-2">
      {options.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-disabled={disabled || undefined}
            className={`inline-flex h-10 items-center gap-2 rounded-full border pl-1.5 pr-3.5 text-sm font-semibold transition-[border-color,background-color,color] duration-150 ${active ? 'border-accent/70 bg-surface-2 text-text' : 'border-line text-muted hover:text-text'}`}
            onClick={() => {
              if (!disabled && !active) onChange(option.value)
            }}
          >
            <TokenIcon symbol={option.symbol} imageUrl={option.logo} size="size-7" />
            {option.symbol}
          </button>
        )
      })}
    </div>
  )
}

export function AssetChip({ symbol, imageUrl, size = 'size-4', logoOnly = false }: { symbol: string; imageUrl?: string; size?: string; logoOnly?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-surface-2 py-0.5 pl-0.5 pr-2 text-xs font-semibold text-text" title={symbol} aria-label={symbol}>
      <TokenIcon symbol={symbol} imageUrl={imageUrl} size={size} />
      {logoOnly ? null : symbol}
    </span>
  )
}
